/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Space,
  Button,
  Popover,
  Tag,
  Row,
  Col,
  Skeleton,
  message,
  Tooltip,
  notification,
  Typography,
  Table
} from 'antd';
import dayjs from 'dayjs';
import { DownOutlined } from '@ant-design/icons';
import FileSaver from 'file-saver';
import SummaryCard from 'components/SummaryCard';
import EbzTable from 'components/EbzTable';
import EbzButton from 'components/EbzButton';
import EbzFilterGroup from 'components/EbzFilterGroup';
import Alert from 'components/Alert';
import { ExportIcon, NotitficationIcon } from 'utils/icons';
import referralsAPI from 'services/referrals';
import merchantAPI from 'services/merchants';
import CustomSkeleton from 'components/CustomSkeleton';
import EbzPopover from 'components/EbzPopover';
import getGtvColumns from './const';

const previousDate = { date: dayjs().subtract(1, 'day').format('YYYY-MM-DD') };
const searchArr = [
  {
    value: 'state',
    label: 'State'
  }
];

function GTVReferralsListPage() {
  // Page State
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.user?.planPermissions);
  const isGTVDashboardReportAllowed = permissions?.can_view_gtv_dashboard_report;
  const isGTVDashboardReportExportAllowed = permissions?.can_export_gtv_dashboard_report;
  const [hasSummaryData, setHasSummaryData] = useState(false);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [data, setData] = useState([]);
  const [customFilterType, setCustomFilterType] = useState([]);
  const [customParam, setCustomParam] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: false,
      total: 0
    }
  });
  const [filtersObj, setFiltersObj] = useState(previousDate);
  const [gtvColumns, setGtvColumns] = useState([]);

  const [api, contextHolder] = notification.useNotification();

  const filtersArr = [
    ...(customParam
      ? [
          {
            label: customParam?.label,
            attr: customParam?.key,
            data: customFilterType
          }
        ]
      : []),
    {
      label: 'Date',
      attr: 'date',
      type: 'Date'
    }
  ];

  const fetchReferralsSummary = async (payload) => {
    const response = await referralsAPI.summary(payload);

    if (!response.success) {
      setHasSummaryData(false);
      Alert('something went wrong', 'Please contact support team for this', 'error');
    }

    setSummary(response?.data ?? []);
  };
  const fetchGtvData = async (pagination, filters) => {
    setLoading(true);
    const response = await referralsAPI.gtvData({
      current_page: pagination.current,
      total_counts: pagination.total,
      filter: { ...filters }
    });

    if (!response.success) {
      Alert('something went wrong', 'Please contact support team for this', 'error');
      setHasData(false);
    }

    setData(response?.data?.gtv ?? []);
    setGtvColumns(
      getGtvColumns(
        response?.data?.custom_param?.label,
        response?.data?.custom_param?.key ?? 'data',
        response?.data?.custom_param
      )
    );
    setCustomParam(response?.data?.custom_param);
    setCustomFilterType(response?.data?.custom_param?.custom_type);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...pagination,
        total: response?.data?.total_count
      }
    });
  };

  const downloadableGtvReport = async () => {
    if (data.length > 0) {
      const response = await merchantAPI.downloadableGTVReport({
        current_page: tableParams.pagination.current,
        total_counts: tableParams.pagination.total,
        filter: { ...filtersObj }
      });
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        Alert('something went wrong', 'Please contact support team for this', 'error');
      } else {
        api.open({
          message: 'Excel Export',
          description: 'Report has been generated.',
          icon: <NotitficationIcon />
        });
        FileSaver.saveAs(response.data, `GTV_Dashboard_report_export.xlsx`);
      }
    } else {
      message.error({
        content: 'No records for Excel export'
      });
    }
  };
  //

  // functions
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter
    });
    fetchGtvData(pagination, filtersObj);
  };

  const applyFiltersCallbackfunc = (a) => {
    setLoading(true);
    const [searchParams, checkboxGroup, date] = a;
    const updatePagination = { ...tableParams.pagination };
    updatePagination.current = 1;
    updatePagination.total = 0;
    const upadtedDate = date || previousDate;
    const filters = {
      ...searchParams,
      ...checkboxGroup,
      ...upadtedDate
    };
    setFiltersObj(filters);
    fetchGtvData(updatePagination, filters);
  };

  const clearFiltersCallBackfun = () => {
    setLoading(true);
    const updatePagination = { ...tableParams.pagination };
    updatePagination.current = 1;
    updatePagination.total = 0;
    setFiltersObj(previousDate);
    fetchGtvData(updatePagination, previousDate);
  };

  // effects
  useEffect(() => {
    if (isGTVDashboardReportAllowed) {
      fetchReferralsSummary();
      fetchGtvData(tableParams.pagination, filtersObj);
    } else {
      navigate('/');
    }
    // Alert('something went wrong', 'Please contact support team for this', 'error');
  }, [permissions]);

  useEffect(() => {
    if (Object.keys(data).length !== 0) {
      setHasData(true);
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(summary).length !== 0) {
      setHasSummaryData(true);
    }
  }, [summary]);

  return (
    <>
      <div
        style={{
          padding: '0 20px',
          margin: '20px 0px'
        }}
      >
        {hasSummaryData === true ? (
          <SummaryCard summary={summary?.summary} updatedAt={summary?.updated_at} />
        ) : (
          <CustomSkeleton />
        )}
      </div>
      <div
        style={{
          padding: '10px 24px'
        }}
      >
        <Space
          direction="horizontal"
          style={{ width: '100%', paddingBottom: '10px', justifyContent: 'space-between' }}
        >
          <Space>
            <Typography style={{ fontSize: '30px', fontWeight: '500' }}>GTV Dashboard</Typography>
            <Tag color="purple">{filtersObj?.date}</Tag>
          </Space>
          <Space style={{ justifyContent: 'flex-end' }}>
            <Space size={10}>
              {loading ? (
                <Button disabled icon={<ExportIcon />} size="large" className="export-btn">
                  Export
                </Button>
              ) : (
                <EbzButton
                  icon={<ExportIcon />}
                  size="large"
                  onClick={downloadableGtvReport}
                  className="export-btn"
                  disabled={!isGTVDashboardReportExportAllowed}
                >
                  Export
                </EbzButton>
              )}
            </Space>
            <Space>
              <EbzPopover
                placement="bottomLeft"
                content={
                  <EbzFilterGroup
                    searchParams={searchArr}
                    filters={filtersArr}
                    disableToday
                    apply={applyFiltersCallbackfunc}
                    clear={clearFiltersCallBackfun}
                  />
                }
                trigger="click"
                overlayStyle={{
                  width: '400px'
                }}
                overlayClassName="ebz-filters-popover"
              >
                <EbzButton size="large" className="ebz-filters-btn">
                  Search & Filter <DownOutlined />
                </EbzButton>
              </EbzPopover>
            </Space>
          </Space>
        </Space>
        <EbzTable
          virtual
          scroll={{ x: 2500, y: 500 }}
          loading={loading}
          onChange={handleTableChange}
          pagination={tableParams.pagination}
          dataSource={data}
          hasBorderTop
          columns={gtvColumns}
        />
      </div>
      {contextHolder}
    </>
  );
}

export default GTVReferralsListPage;
